import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Lock, Unlock, Plus, Trash2, AlertTriangle, GripVertical, X, RefreshCw,
  ChevronDown, ChevronRight, RotateCcw,
} from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, arrayMove, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useBoqTaxonomies, BRIO_TRADE_TAXONOMY, TRADE_TYPE_TAXONOMY, TRADE_GROUP_TAXONOMY,
} from '../hooks/useBoqTaxonomies';
import { BrioCard, BrioEmptyState, BrioButton, BrioInput } from '../../components/BrioComponents';
import { formatSeriesNumber, formatSeriesRange } from '../utils/itemCodeGenerator';
import {
  distributeSeries, bandWithinSubtrade, cascadeSubtradeRanges, nextSeriesStart,
} from '../utils/seriesDistribution';
import { caseTypedText } from '../utils/tradeTypeCase';
import { chapterTickState } from '../utils/tradeGroupSubtrades';

// Depth at which a taxonomy's nodes carry a code name, or null for the ones
// that carry none. The trade group supplies the first segment of a library item
// code from its top level; the canonical taxonomy supplies the middle segment
// from its sub-trades, so the box sits one level down there.
const SHORT_CODE_DEPTH = {
  [TRADE_GROUP_TAXONOMY]: 0,
  [BRIO_TRADE_TAXONOMY]: 1,
};
// How many letters a generated item code takes from each: TRADE.SUBTRADE is
// 2 then 3. A code of the wrong length saves fine but can never build an item
// code, so the box says so rather than letting it look correct.
const SHORT_CODE_LENGTH = {
  [TRADE_GROUP_TAXONOMY]: 2,
  [BRIO_TRADE_TAXONOMY]: 3,
};
const GRID_WITH_CODE = '120px 1fr 1fr 112px';
// The Trade group taxonomy adds a Subtrades column left of the code name: the
// canonical sub-trades each trade group offers on New item.
const GRID_WITH_SUBTRADES = '120px 1fr 1fr 210px 112px';
const GRID_PLAIN = '120px 1fr 1fr';
// Trade types list under their anchor rather than by node code, so the row is
// grip / label / series / delete.
const GRID_TRADE_TYPE = '22px 1fr 176px 60px 32px';

const CLASH_COLOR = '#a94442';
const EMPTY_SET = new Set();

// ============================================================================
// Code-name uniqueness
// ============================================================================
// A code name has to be unique within its group, not across the taxonomy: the
// same fragment under two different chapters builds two different item codes,
// so only a collision inside one group is ambiguous. What "group" means
// differs per taxonomy, and there is no generic fallback that gets all three
// right — `trade_group.maps_to_node_id` points at a brio chapter, so treating
// it as the group key would file two unrelated trade groups together.
const groupKeyFor = (taxonomyCode, node) => {
  if (taxonomyCode === BRIO_TRADE_TAXONOMY) return node.parentId || '__root__';
  if (taxonomyCode === TRADE_TYPE_TAXONOMY) return node.mapsToNodeId || '__none__';
  return '__all__';   // trade_group is one flat list
};

// node id → labels of the other nodes in its group wearing the same code.
// Absent for everything unique. Existing clashes are surfaced rather than
// silently tolerated: several arrived by SQL seed, before any guard existed.
const buildClashMap = (taxonomyCode, eligible) => {
  const byGroupCode = new Map();
  eligible.forEach((n) => {
    if (!n.shortCode) return;
    const key = `${groupKeyFor(taxonomyCode, n)}::${n.shortCode}`;
    if (!byGroupCode.has(key)) byGroupCode.set(key, []);
    byGroupCode.get(key).push(n);
  });
  const clashes = new Map();
  byGroupCode.forEach((nodes) => {
    if (nodes.length < 2) return;
    nodes.forEach((n) => clashes.set(n.id, nodes.filter((o) => o.id !== n.id).map((o) => o.label)));
  });
  return clashes;
};

// Returns the label already wearing `code` in this node's group, or null.
// Checked before the write so a duplicate is refused rather than saved; the
// guard trigger refuses it server-side too, for anything that gets past here.
const makeTakenBy = (taxonomyCode, eligible) => (nodeId, code) => {
  if (!code) return null;
  const self = eligible.find((n) => n.id === nodeId);
  if (!self) return null;
  const key = groupKeyFor(taxonomyCode, self);
  const hit = eligible.find((n) => (
    n.id !== nodeId && n.shortCode === code && groupKeyFor(taxonomyCode, n) === key
  ));
  return hit ? hit.label : null;
};

// ============================================================================
// Code-name cell
// ============================================================================
// The fragment this node contributes to a library item code: 2 or 3 letters.
// Saves on blur so there is no button per row. Recorded only — nothing
// generates an item code from it yet.
//
// No placeholder: an empty box means no code has been entered, and a greyed
// example in every empty row reads like data that is already there.
function ShortCodeCell({ node, onSave, disabled, lockedReason, clashesWith = [], takenBy, expectLength }) {
  const [value, setValue] = useState(node.shortCode || '');
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  // A clash already in the data stays red whether the column is locked or not
  // — it is a problem to see, not only one to prevent. Once the box is being
  // edited away from the saved value the warning no longer applies to what is
  // typed, so it drops until the edit is committed.
  const showClash = clashesWith.length > 0 && value === (node.shortCode || '');
  const clashText = showClash
    ? `Also used by ${clashesWith.join(', ')} in the same chapter / sub-trade`
    : null;
  // A saved code of the wrong length is not refused — it may predate the
  // generator — but it can never build an item code, so it reads as a problem.
  const wrongLength = !!expectLength && !!node.shortCode
    && node.shortCode.length !== expectLength && value === node.shortCode;
  const lengthText = wrongLength
    ? `${node.shortCode.length} letters — an item code needs exactly ${expectLength} here, so this cannot generate one`
    : null;
  const bad = showClash || wrongLength || !!err;

  const commit = async () => {
    const next = value.trim().toUpperCase();
    if (next === (node.shortCode || '')) { setValue(next); return; }
    // Refuse a duplicate before the write. The guard trigger refuses it
    // server-side too, so a stale list here cannot let one through.
    const conflict = takenBy ? takenBy(node.id, next) : null;
    if (conflict) {
      setErr(`Already used by ${conflict}.`);
      setValue(node.shortCode || '');
      return;
    }
    setSaving(true);
    const res = await onSave(node.id, next);
    setSaving(false);
    if (res.success) { setValue(next); setErr(null); }
    else { setErr(res.error); setValue(node.shortCode || ''); }
  };

  return (
    <div>
      <BrioInput
        value={value}
        disabled={disabled || saving}
        maxLength={3}
        onChange={(e) => { setValue(e.target.value.toUpperCase()); if (err) setErr(null); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        title={clashText || lengthText || err || lockedReason
          || (expectLength ? `Code name fragment — ${expectLength} letters` : 'Code name fragment for this node')}
        style={{
          width: 72, fontSize: '0.78rem', padding: '0.15rem 0.35rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          textAlign: 'center', letterSpacing: '0.05em',
          // A locked box still has to read: dim the frame, not the value. A
          // clash outranks that — red survives the lock.
          background: bad ? 'rgba(169,68,66,0.08)' : (disabled ? 'transparent' : undefined),
          color: bad ? CLASH_COLOR : undefined,
          borderColor: bad ? CLASH_COLOR : (disabled ? 'var(--brio-border-subtle, #eee)' : undefined),
        }}
      />
      {(err || clashText || lengthText) && (
        <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>
          {err || (clashText ? `Duplicate of ${clashesWith.join(', ')}` : `Needs ${expectLength} letters`)}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Series cell
// ============================================================================
// The band of sequence numbers a trade type's library items draw from. Both
// ends or neither; blank means the type cannot mint a code yet. Saves on blur,
// same as a code name, and the same lock governs it.
// A number input cannot render a leading zero, so the two boxes are text with
// a numeric keypad: digits only while typing, padded to the code's 4 on blur.
function SeriesCell({ node, onSave, disabled, lockedReason, validate, title }) {
  const [from, setFrom] = useState(formatSeriesNumber(node.seriesStart));
  const [to, setTo] = useState(formatSeriesNumber(node.seriesEnd));
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFrom(formatSeriesNumber(node.seriesStart));
    setTo(formatSeriesNumber(node.seriesEnd));
  };

  // The boxes hold padded strings and the column holds integers, so compare on
  // the number. Without that, 0001 against a stored 1 reads as an edit and
  // fires a save on every blur.
  const same = (shown, stored) => (
    shown === '' ? stored === null || stored === undefined : Number(shown) === Number(stored)
  );

  const commit = async (nextFrom, nextTo) => {
    if (same(nextFrom, node.seriesStart) && same(nextTo, node.seriesEnd)) return;
    // One end alone is not a series. Wait for the other rather than rejecting
    // a half-typed range the moment focus moves between the two boxes.
    if ((nextFrom === '') !== (nextTo === '')) return;
    // Say no here rather than after a round trip. The database checks the same
    // thing; this only spares the trip and puts the reason next to the box.
    const refusal = nextFrom === '' ? null : validate?.(Number(nextFrom), Number(nextTo));
    if (refusal) { setErr(refusal); reset(); return; }
    setSaving(true);
    const res = await onSave(node.id, nextFrom, nextTo);
    setSaving(false);
    if (res.success) setErr(null);
    else { setErr(res.error); reset(); }
  };

  const box = (value, setValue, other, isFrom, placeholder) => (
    <BrioInput
      type="text"
      inputMode="numeric"
      maxLength={4}
      value={value}
      disabled={disabled || saving}
      placeholder={placeholder}
      // Padding waits for blur: re-padding on every keystroke would fight a
      // backspace, turning 0012 into 0001 instead of letting it become 1.
      onChange={(e) => {
        setValue(e.target.value.replace(/\D/g, '').slice(0, 4));
        if (err) setErr(null);
      }}
      onBlur={() => {
        const padded = formatSeriesNumber(value);
        setValue(padded);
        commit(isFrom ? padded : other, isFrom ? other : padded);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      title={lockedReason || title || 'Sequence numbers this trade type\'s items draw from, as they appear in an item code'}
      style={{
        width: 62, fontSize: '0.78rem', padding: '0.15rem 0.3rem',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        textAlign: 'center',
        background: err ? 'rgba(169,68,66,0.08)' : (disabled ? 'transparent' : undefined),
        borderColor: err ? CLASH_COLOR : (disabled ? 'var(--brio-border-subtle, #eee)' : undefined),
      }}
    />
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {box(from, setFrom, to, true, 'from')}
        <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem' }}>–</span>
        {box(to, setTo, from, false, 'to')}
      </div>
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// The lock that opens a code-name column for editing. One per column: it is
// component state in the section that owns it, so leaving the page or
// reloading re-locks, and one column says nothing about another.
function CodeNameHeader({ canCurate, unlocked, onToggle, label = 'Code name' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
      <span>{label}</span>
      <button
        type="button"
        disabled={!canCurate}
        onClick={onToggle}
        title={!canCurate
          ? `Only library curators may edit ${label.toLowerCase()}s`
          : (unlocked ? `Lock the ${label.toLowerCase()} column` : `Unlock to edit ${label.toLowerCase()}s`)}
        style={{
          display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none',
          padding: 2, lineHeight: 0,
          cursor: canCurate ? 'pointer' : 'not-allowed',
          color: (canCurate && unlocked) ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text-muted)',
          opacity: canCurate ? 1 : 0.5,
        }}
      >
        {(canCurate && unlocked) ? <Unlock size={13} /> : <Lock size={13} />}
      </button>
    </div>
  );
}

const lockedReasonFor = (canCurate, unlocked) => (!canCurate
  ? 'Only library curators may edit code names'
  : (unlocked ? null : 'Locked — use the lock above the column to edit'));

// ============================================================================
// Label cell (trade group)
// ============================================================================
// A trade group's name. A curator double clicks it to rename: Enter or
// clicking away saves, Escape cancels. Typed as written, with no casing rule:
// these labels carry initialisms and dashes (MEP — HVAC) that a rule would
// mangle. Only the label changes; the node keeps its id, code and code name,
// so no item tag and no item code moves.
function LabelCell({ node, canRename, onRename, style }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(node.label);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);
  // Enter and Escape both end in a blur, which would otherwise save twice or
  // save the edit Escape meant to throw away.
  const doneRef = useRef(false);

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
  }, [editing]);

  const start = () => {
    if (!canRename) return;
    doneRef.current = false;
    setValue(node.label);
    setErr(null);
    setEditing(true);
  };
  const cancel = () => { doneRef.current = true; setErr(null); setEditing(false); };
  const commit = async () => {
    if (doneRef.current || busy) return;
    const next = value.trim();
    if (!next || next === node.label) { cancel(); return; }
    doneRef.current = true;
    setBusy(true);
    const res = await onRename(node.id, next);
    setBusy(false);
    if (res.success) { setEditing(false); setErr(null); } else {
      // Keep the box open on what was typed so it can be fixed, not retyped.
      doneRef.current = false;
      setErr(res.error);
    }
  };

  if (!editing) {
    return (
      <div
        onDoubleClick={start}
        title={canRename ? 'Double click to rename' : undefined}
        style={{ ...style, cursor: canRename ? 'text' : undefined, userSelect: canRename ? 'none' : undefined }}
      >
        {node.label}
      </div>
    );
  }
  return (
    <div style={style}>
      <BrioInput
        ref={inputRef}
        value={value}
        disabled={busy}
        onChange={(e) => { setValue(e.target.value); if (err) setErr(null); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') cancel();
        }}
        onBlur={commit}
        style={{ fontSize: '0.85rem', padding: '0.15rem 0.4rem', maxWidth: 420 }}
      />
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// ============================================================================
// Subtrades checklist (trade group)
// ============================================================================
// The canonical sub-trades a trade group offers on New item and in the Edit
// modal. Closed, it reads as the ticked sub-trades' code names in chapter
// order; open, it lists every chapter with its sub-trades indented under it.
// Ticking a chapter ticks every sub-trade under it, and its box shows
// indeterminate while only part are ticked. A trade group with nothing ticked
// offers every sub-trade, so the first tick is what starts narrowing it. Each
// tick saves on its own; the column lock governs them.
function ChapterCheckbox({ state, disabled, onChange }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === 'some'; }, [state]);
  return <input ref={ref} type="checkbox" checked={state === 'all'} disabled={disabled} onChange={onChange} />;
}

function SubtradeChecklist({ tradeGroup, chapters, ticked, onToggle, disabled, lockedReason }) {
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [pending, setPending] = useState(() => new Set());
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const chosen = chapters.flatMap((c) => c.subs).filter((n) => ticked.has(String(n.id)));
  const summary = chosen.length
    ? chosen.map((n) => n.shortCode || n.code).join(', ')
    : 'None';

  const toggle = async (ids, on) => {
    if (disabled || ids.length === 0 || ids.some((id) => pending.has(id))) return;
    setErr(null);
    setPending((p) => { const n = new Set(p); ids.forEach((id) => n.add(id)); return n; });
    const res = await onToggle(tradeGroup.id, ids, on);
    setPending((p) => { const n = new Set(p); ids.forEach((id) => n.delete(id)); return n; });
    if (!res.success) setErr(res.error);
  };

  const rowStyle = (busy) => ({
    display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.2rem 0.6rem',
    fontSize: '0.8rem', cursor: disabled ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={chosen.length ? chosen.map((n) => n.label).join('\n') : 'No sub-trade ticked: every sub-trade is offered'}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4,
          padding: '0.15rem 0.4rem', fontSize: '0.74rem', textAlign: 'left',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          border: `1px solid ${disabled ? 'var(--brio-border-subtle, #eee)' : 'var(--brio-border, #ddd)'}`,
          borderRadius: 4, background: 'transparent', cursor: 'pointer',
          color: chosen.length ? 'var(--brio-text, #333)' : 'var(--brio-text-muted)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <ChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 2px)', zIndex: 20, width: 340,
            maxHeight: 380, overflowY: 'auto', padding: '0.35rem 0',
            background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border, #ddd)',
            borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }}
        >
          {lockedReason && (
            <div style={{ padding: '0.2rem 0.6rem 0.35rem', fontSize: '0.7rem', color: 'var(--brio-text-muted)' }}>
              {lockedReason}
            </div>
          )}
          {chapters.map((c) => {
            const ids = c.subs.map((n) => String(n.id));
            const state = chapterTickState(ids, ticked);
            const busy = ids.some((id) => pending.has(id));
            return (
              <div key={c.label}>
                <label style={{ ...rowStyle(busy), fontWeight: 600, marginTop: '0.2rem' }}>
                  <ChapterCheckbox state={state} disabled={disabled || busy}
                    onChange={() => toggle(ids, state !== 'all')} />
                  <span>{c.number != null ? `${c.number}. ` : ''}{c.label}</span>
                </label>
                {c.subs.map((n) => {
                  const id = String(n.id);
                  return (
                    <label key={id} style={{ ...rowStyle(pending.has(id)), paddingLeft: '1.9rem' }}>
                      <input
                        type="checkbox"
                        checked={ticked.has(id)}
                        disabled={disabled || pending.has(id)}
                        onChange={() => toggle([id], !ticked.has(id))}
                      />
                      <code style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)', minWidth: 30 }}>{n.shortCode}</code>
                      <span>{n.label}</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// ============================================================================
// Taxonomy listing
// ============================================================================
// Read-only apart from the code name, which a curator edits in place.
// ============================================================================
function NodeRow({ node, depth, shortCodeDepth, editable: columnEditable, lockedReason,
  onSetShortCode, clashes, takenBy, expectLength, rename, tg }) {
  const hasCodeColumn = shortCodeDepth !== null;
  const grid = tg ? GRID_WITH_SUBTRADES : (hasCodeColumn ? GRID_WITH_CODE : GRID_PLAIN);
  // The box sits at one depth only, but the column is reserved on every row of
  // the taxonomy so it stays a column. The indent goes on the two text cells
  // rather than the row, or a child's code name would sit 20px right of its
  // parent's.
  const atCodeDepth = depth === shortCodeDepth;
  const indent = depth * 20;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: grid,
          gap: '0.75rem',
          alignItems: 'center',
          padding: '0.35rem 0',
          paddingLeft: hasCodeColumn ? 0 : indent,
          borderBottom: '1px solid var(--brio-border-subtle, #eee)',
          opacity: node.isActive ? 1 : 0.5,
        }}
      >
        <code style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', paddingLeft: hasCodeColumn ? indent : 0 }}>
          {node.code}
        </code>
        {rename ? (
          <LabelCell node={node} canRename={rename.canRename} onRename={rename.onRename}
            style={{ fontSize: '0.9rem', paddingLeft: hasCodeColumn ? indent : 0 }} />
        ) : (
          <div style={{ fontSize: '0.9rem', paddingLeft: hasCodeColumn ? indent : 0 }}>{node.label}</div>
        )}
        <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)' }}>
          {node.altLabelIn ? `IN: ${node.altLabelIn}` : ''}
        </div>
        {tg && (
          <SubtradeChecklist tradeGroup={node} chapters={tg.chapters}
            ticked={tg.byGroup.get(String(node.id)) || EMPTY_SET} onToggle={tg.onToggle}
            disabled={!tg.editable} lockedReason={tg.lockedReason} />
        )}
        {hasCodeColumn && (atCodeDepth ? (
          <ShortCodeCell node={node} disabled={!columnEditable}
            lockedReason={lockedReason} onSave={onSetShortCode}
            clashesWith={clashes.get(node.id) || []} takenBy={takenBy}
            expectLength={expectLength} />
        ) : <div />)}
      </div>
      {node.children.map((child) => (
        <NodeRow key={child.id} node={child} depth={depth + 1}
          shortCodeDepth={shortCodeDepth} editable={columnEditable}
          lockedReason={lockedReason} onSetShortCode={onSetShortCode}
          clashes={clashes} takenBy={takenBy} expectLength={expectLength}
          rename={rename} tg={tg} />
      ))}
    </>
  );
}

function TaxonomySection({
  taxonomy, expanded, onToggle, canCurate, onSetShortCode, onRename,
  subtradeChapters = [], subtradesByGroup, onToggleSubtrades,
}) {
  const shortCodeDepth = SHORT_CODE_DEPTH[taxonomy.code] ?? null;
  const hasCodeColumn = shortCodeDepth !== null;
  // Each taxonomy holds its own lock, and it is component state rather than
  // anything persisted: leaving the page or reloading re-locks the column on
  // its own. A code name is a deliberate edit, not something to fall into by
  // clicking the wrong row.
  const [unlocked, setUnlocked] = useState(false);
  const columnEditable = canCurate && unlocked;
  const lockedReason = lockedReasonFor(canCurate, unlocked);
  // Trade group labels rename in place; every other taxonomy lists read-only.
  const rename = taxonomy.code === TRADE_GROUP_TAXONOMY ? { canRename: canCurate, onRename } : null;
  // The Trade group taxonomy carries the Subtrades checklist, with a lock of
  // its own: ticking a box changes what New item offers, which is a different
  // edit from typing a code name.
  const showTg = taxonomy.code === TRADE_GROUP_TAXONOMY;
  const [tgUnlocked, setTgUnlocked] = useState(false);
  const tg = showTg ? {
    chapters: subtradeChapters,
    byGroup: subtradesByGroup || new Map(),
    onToggle: onToggleSubtrades,
    editable: canCurate && tgUnlocked,
    lockedReason: !canCurate
      ? 'Only library curators may change these'
      : (tgUnlocked ? null : 'Locked — use the lock above the column to edit'),
  } : null;

  // Only the rows that carry a code name take part: a chapter and a sub-trade
  // never compete for the same fragment because only one of them has a box.
  const { clashes, takenBy } = useMemo(() => {
    if (shortCodeDepth === null) return { clashes: new Map(), takenBy: null };
    const eligible = taxonomy.nodes.filter((n) => (shortCodeDepth === 0 ? !n.parentId : !!n.parentId));
    return {
      clashes: buildClashMap(taxonomy.code, eligible),
      takenBy: makeTakenBy(taxonomy.code, eligible),
    };
  }, [taxonomy, shortCodeDepth]);

  return (
    <BrioCard className="brio-mb-m">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={onToggle}
      >
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>
            {taxonomy.name} <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>· {taxonomy.nodes.length} nodes</span>
          </h3>
          {taxonomy.description && (
            <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)', marginTop: '0.25rem' }}>
              {taxonomy.description}
            </div>
          )}
          <code style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>{taxonomy.code}</code>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
          {expanded ? '▾ collapse' : '▸ expand'}
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: '0.75rem' }}>
          {hasCodeColumn && (
            <div style={{
              display: 'grid', gridTemplateColumns: showTg ? GRID_WITH_SUBTRADES : GRID_WITH_CODE, gap: '0.75rem',
              padding: '0 0 0.35rem', borderBottom: '2px solid var(--brio-border, #ddd)',
              fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)',
            }}>
              <div>Code</div><div>Label</div><div />
              {showTg && (
                <CodeNameHeader label="Subtrades" canCurate={canCurate} unlocked={tgUnlocked}
                  onToggle={() => setTgUnlocked((v) => !v)} />
              )}
              <CodeNameHeader canCurate={canCurate} unlocked={unlocked}
                onToggle={() => setUnlocked((v) => !v)} />
            </div>
          )}
          {taxonomy.tree.length === 0 ? (
            <BrioEmptyState>No nodes seeded yet.</BrioEmptyState>
          ) : (
            taxonomy.tree.map((n) => (
              <NodeRow key={n.id} node={n} depth={0}
                shortCodeDepth={shortCodeDepth} editable={columnEditable}
                lockedReason={lockedReason} onSetShortCode={onSetShortCode}
                clashes={clashes} takenBy={takenBy}
                expectLength={SHORT_CODE_LENGTH[taxonomy.code]}
                rename={rename} tg={tg} />
            ))
          )}
        </div>
      )}
    </BrioCard>
  );
}

// ============================================================================
// Trade type — its own editable section
// ============================================================================
// One level, listed flat: every canonical sub-trade in chapter order, each with
// the trade types filed under it and an Add of its own. A type says which
// sub-trade it belongs to via maps_to_node_id, which is what supplies the
// middle segment of a library item code — so the sub-trade a type is added
// under is never a free choice made at the bottom of the page, it is the row
// the Add was clicked on. A type whose anchor has since gone still renders,
// under "Unassigned", rather than vanishing.

// A trade type is written sentence case: one capital at the front, the rest
// lower. Caps lock is the deliberate escape hatch, because a few types are
// initialisms (PCC, RCC) that a lowercasing rule would otherwise mangle. The
// case is decided per keystroke by caseTypedText, so caps lock capitalises only
// the letters typed while it is down and never recases the rest of the box.
//
// Shared by the Add box and the rename box. Caps lock lives in a ref, not
// state: keydown and the change it causes run in the same turn, and a state
// update would not be visible to the change handler in time to decide the case.
function useTradeTypeName(initial = '') {
  const [label, setLabel] = useState(initial);
  const inputRef = useRef(null);
  const capsRef = useRef(false);
  const caretRef = useRef(null);

  // The case transform never changes the string's length, so restoring the
  // caret is exact. Without it, a letter recased mid-word rewrites the value
  // and the caret jumps to the end.
  useLayoutEffect(() => {
    if (caretRef.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  });

  const readCaps = (e) => { capsRef.current = e.getModifierState('CapsLock'); };

  const onChange = (e) => {
    caretRef.current = e.target.selectionStart;
    const raw = e.target.value;
    setLabel((prev) => caseTypedText(prev, raw, capsRef.current));
  };

  return { label, setLabel, inputRef, readCaps, onChange };
}

const NAME_HINT = 'Sentence case as you type. Caps lock capitalises only the letters typed while it is on, for an initialism such as PCC.';

// The box that appears when Add is clicked on a sub-trade. It stays open and
// clears after a successful add, so several types can be entered in a row;
// Escape closes it.
function NewTradeTypeInput({ onAdd, onClose }) {
  const { label, setLabel, inputRef, readCaps, onChange } = useTradeTypeName();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { inputRef.current?.focus(); }, [inputRef]);

  const submit = async () => {
    const value = label.trim();
    if (!value || busy) return;
    setBusy(true);
    const res = await onAdd(value);
    setBusy(false);
    if (res.success) { setLabel(''); setErr(null); } else setErr(res.error);
  };

  return (
    <div style={{ padding: '0.3rem 0 0.4rem', paddingLeft: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <BrioInput
          ref={inputRef}
          value={label}
          placeholder="Name the trade type…"
          disabled={busy}
          onKeyDown={(e) => {
            readCaps(e);
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') onClose();
          }}
          onKeyUp={readCaps}
          onChange={(e) => { onChange(e); if (err) setErr(null); }}
          style={{ fontSize: '0.85rem', padding: '0.2rem 0.45rem', maxWidth: 280 }}
        />
        <BrioButton variant="secondary" onClick={submit} disabled={busy || !label.trim()}
          style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}>
          {busy ? 'Adding…' : 'Save'}
        </BrioButton>
        <button type="button" onClick={onClose} title="Close"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: 'var(--brio-text-muted)', display: 'inline-flex',
          }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ fontSize: '0.68rem', color: 'var(--brio-text-muted)', marginTop: 2 }}>
        {NAME_HINT}
      </div>
      {err && <div style={{ fontSize: '0.72rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// A sub-trade's own range. Setting it splits the range evenly across the trade
// types under it, in their drag order.
//
// The split runs only here, when the range is set or changed. Adding or
// deleting a type later deliberately leaves every band alone: a band that
// shifted on its own would start issuing numbers inside a band whose codes are
// already quoted in project BOQs, and an item code cannot be changed after the
// fact. A type added later gets its band by hand, within this range.
// Saves a sub-trade's range and splits it across the trade types under it,
// then numbers every sub-trade below it straight on from it (each keeping its
// own count, wrapping to 0001 past 9999, skipping any with no range) and
// splits each of those again. Shared by the range boxes, the No box and the
// refresh, so all of them ask one confirm listing everything that will move.
function makeSubtradeRangeSave(row, below, api, onError) {
  return async (subtradeId, from, to) => {
    if (from === '' || from === null || from === undefined) {
      // Clearing the range clears the bands under it too, so say so: the types
      // are left with no numbers to draw from until someone sets them again.
      // Nothing below moves: a cleared range gives the next one nothing to
      // follow on from.
      const held = row.types.filter((t) => t.seriesStart != null);
      if (held.length && !window.confirm(
        `Clear the range on "${row.label}"?\n\nThis also clears the series on `
        + `${held.length} trade type${held.length === 1 ? '' : 's'} under it, so no new `
        + `library item can be coded there until they are set again.\n\nNo existing item code changes.`,
      )) return { success: false, error: null };
      return api.setSubtradeSeries(subtradeId, null, null, []);
    }

    const { bands, error } = distributeSeries({
      start: Number(from), end: Number(to), types: row.types,
    });
    if (error) return { success: false, error };

    const { moves, error: cascadeError } = cascadeSubtradeRanges({ anchorEnd: Number(to), below });
    if (cascadeError) return { success: false, error: cascadeError };
    // Every moved sub-trade has to split again: its trade types must sit
    // inside its range, and the database refuses a band that does not.
    const plans = [];
    for (const m of moves) {
      const sub = below.find((b) => b.id === m.id);
      const split = distributeSeries({ start: m.from, end: m.to, types: sub.types });
      if (split.error) return { success: false, error: `${sub.label}: ${split.error}` };
      plans.push({ ...m, bands: split.bands, typeCount: sub.types.length });
    }

    // Only warn about bands that are actually being moved. A type whose band
    // already matches what the split would give it loses nothing.
    const overwritten = bands.filter((b) => {
      const cur = row.types.find((t) => t.id === b.id);
      return cur?.seriesStart != null
        && (cur.seriesStart !== b.seriesStart || cur.seriesEnd !== b.seriesEnd);
    });
    const parts = [];
    if (overwritten.length) {
      const lines = overwritten.map((b) => {
        const cur = row.types.find((t) => t.id === b.id);
        return `  ${b.label}: ${formatSeriesRange(cur.seriesStart, cur.seriesEnd)}`
          + ` becomes ${formatSeriesRange(b.seriesStart, b.seriesEnd)}`;
      }).join('\n');
      parts.push(
        `Splitting ${formatSeriesRange(Number(from), Number(to))} across `
        + `${bands.length} trade type${bands.length === 1 ? '' : 's'} replaces `
        + `${overwritten.length} series that ${overwritten.length === 1 ? 'was' : 'were'} set by hand:\n\n`
        + lines,
      );
    }
    if (plans.length) {
      const lines = plans.map((p) => `  ${p.label}: ${formatSeriesRange(p.oldFrom, p.oldTo)}`
        + ` becomes ${formatSeriesRange(p.from, p.to)}`
        + (p.typeCount ? ` (${p.typeCount} trade type${p.typeCount === 1 ? '' : 's'} split again)` : ''))
        .join('\n');
      parts.push(
        `${plans.length} sub-trade${plans.length === 1 ? '' : 's'} below `
        + `${plans.length === 1 ? 'moves' : 'move'} to follow on:\n\n${lines}`,
      );
    }
    if (parts.length && !window.confirm(
      `${parts.join('\n\n')}\n\nNo existing item code changes, but new items will be `
      + `numbered from the new ranges and bands. Go ahead?`,
    )) return { success: false, error: null };

    const quiet = plans.length > 0;
    const res = await api.setSubtradeSeries(subtradeId, Number(from), Number(to), bands, { refresh: !quiet });
    if (!res.success) {
      if (quiet) await api.refresh({ quiet: true });
      if (res.error) onError(res.error);
      return res;
    }
    for (const p of plans) {
      const r = await api.setSubtradeSeries(p.id, p.from, p.to, p.bands, { refresh: false });
      if (!r.success) {
        await api.refresh({ quiet: true });
        const error = `${p.label} could not move to ${formatSeriesRange(p.from, p.to)}: ${r.error}`
          + ' The sub-trades above it were saved; the ones from here down were not.';
        onError(error);
        return { success: false, error };
      }
    }
    if (quiet) await api.refresh({ quiet: true });
    return res;
  };
}

function SubtradeSeriesCell({ row, below, api, disabled, lockedReason, onError }) {
  const node = { id: row.key, seriesStart: row.seriesStart, seriesEnd: row.seriesEnd };
  const save = makeSubtradeRangeSave(row, below, api, onError);

  // The refresh re-runs the split on the range already stored. Typing the same
  // range back into the boxes is a no-op by design (nothing changed), so after
  // a count is edited or a type added this is the only way to ask for a new
  // split — and it stays something a curator presses, never automatic.
  const [redoing, setRedoing] = useState(false);
  const redo = async () => {
    if (redoing || row.seriesStart == null) return;
    setRedoing(true);
    const res = await save(row.key, row.seriesStart, row.seriesEnd);
    setRedoing(false);
    if (!res.success && res.error) onError(res.error);
  };

  const canRedo = !disabled && row.seriesStart != null && row.types.length > 0;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem' }}>
      <SeriesCell
        key={`${row.seriesStart}-${row.seriesEnd}-${row.types.length}`}
        node={node}
        onSave={save}
        disabled={disabled}
        lockedReason={lockedReason}
        title={row.types.length
          ? `Split across the ${row.types.length} trade type`
            + `${row.types.length === 1 ? '' : 's'} under this sub-trade`
          : 'Recorded now, split across trade types once there are some'}
      />
      <button
        type="button"
        disabled={!canRedo || redoing}
        onClick={redo}
        title={disabled
          ? lockedReason
          : (row.seriesStart == null
            ? 'Set a range first'
            : (row.types.length
              ? 'Split this range across the trade types again, honouring their counts'
              : 'No trade types under this sub-trade yet'))}
        style={{
          display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none',
          padding: 2, marginTop: 2, lineHeight: 0,
          cursor: canRedo && !redoing ? 'pointer' : 'not-allowed',
          color: 'var(--brio-text-muted)',
          opacity: canRedo ? 1 : 0.35,
        }}
      >
        <RefreshCw size={13} style={redoing ? { opacity: 0.5 } : undefined} />
      </button>
    </div>
  );
}

// How many numbers a sub-trade's range holds. Typing a count keeps the start
// where it is and moves the end to fit, then saves through the same path as
// the range boxes, so the split and its confirms are identical. A row with no
// range has no start to count from, so the box waits for one. It never
// touches any other sub-trade's range.
//
// A sub-trade with no range yet takes one from its count: it starts right
// after the nearest sub-trade above that has a range (aboveEnd), or at 0001
// when none does or the count would run past 9999.
function SubtradeCountCell({ row, below, aboveEnd, api, disabled, lockedReason, onError }) {
  const current = row.seriesStart != null ? String(row.seriesEnd - row.seriesStart + 1) : '';
  const [value, setValue] = useState(current);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const noRange = row.seriesStart == null;

  const commit = async () => {
    if (value === current) return;
    const n = Number(value);
    if (value === '' || n < 1) { setValue(current); return; }
    if (n > 9999) {
      setErr('A range can hold at most 9999 numbers, 0001 to 9999.');
      setValue(current);
      return;
    }
    const start = noRange ? nextSeriesStart(aboveEnd, n) : row.seriesStart;
    const end = start + n - 1;
    if (end > 9999) {
      setErr(`Starting at ${formatSeriesNumber(row.seriesStart)}, the most this range can hold is `
        + `${9999 - row.seriesStart + 1}.`);
      setValue(current);
      return;
    }
    setSaving(true);
    const res = await makeSubtradeRangeSave(row, below, api, onError)(row.key, start, end);
    setSaving(false);
    if (res.success) setErr(null);
    else { if (res.error) setErr(res.error); setValue(current); }
  };

  return (
    <div>
      <BrioInput
        type="text"
        inputMode="numeric"
        maxLength={5}
        value={value}
        disabled={disabled || saving}
        onChange={(e) => { setValue(e.target.value.replace(/\D/g, '').slice(0, 5)); if (err) setErr(null); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        title={lockedReason
          || (noRange
            ? 'How many numbers this sub-trade holds. It starts right after the sub-trade above.'
            : 'How many numbers this sub-trade holds. The start stays put and the end moves to fit.')}
        style={{
          width: 54, fontSize: '0.78rem', padding: '0.15rem 0.3rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          textAlign: 'center',
          background: err ? 'rgba(169,68,66,0.08)' : (disabled ? 'transparent' : undefined),
          borderColor: err ? CLASH_COLOR : (disabled ? 'var(--brio-border-subtle, #eee)' : undefined),
        }}
      />
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// Starts a sub-trade's numbering again at 0001, keeping its count, and lets
// every sub-trade below follow on from it. Same save path, so the same single
// confirm lists every range that moves. Needs a range: with no count there is
// nothing to restart.
function SubtradeRestartButton({ row, below, api, disabled, lockedReason, onError }) {
  const [busy, setBusy] = useState(false);
  const noRange = row.seriesStart == null;
  const off = disabled || noRange || busy;

  const restart = async () => {
    if (off) return;
    const count = row.seriesEnd - row.seriesStart + 1;
    setBusy(true);
    await makeSubtradeRangeSave(row, below, api, onError)(row.key, 1, count);
    setBusy(false);
  };

  return (
    <button
      type="button"
      disabled={off}
      onClick={restart}
      title={disabled
        ? lockedReason
        : (noRange
          ? 'Give this sub-trade a count first'
          : 'Restart numbering here at 0001; every sub-trade below follows on')}
      aria-label={`Restart numbering at 0001 from ${row.label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none',
        padding: 4, lineHeight: 0,
        cursor: off ? 'not-allowed' : 'pointer',
        color: 'var(--brio-text-muted)',
        opacity: (disabled || noRange) ? 0.35 : 1,
      }}
    >
      <RotateCcw size={13} style={busy ? { opacity: 0.5 } : undefined} />
    </button>
  );
}

// How many numbers a trade type asks for in the next split. Empty means it
// shares the balance equally with the other types that ask for nothing.
//
// Saving a count does NOT re-split: the numbers only move when a curator sets
// the sub-trade's range or presses the refresh beside it. A count that changed
// a band the moment it was typed would shift codes already quoted in project
// BOQs out from under whoever was reading them.
function CountCell({ node, onSave, disabled, lockedReason }) {
  const [value, setValue] = useState(node.seriesWidth == null ? '' : String(node.seriesWidth));
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const current = node.seriesWidth == null ? '' : String(node.seriesWidth);
    if (value === current) return;
    setSaving(true);
    const res = await onSave(node.id, value);
    setSaving(false);
    if (res.success) setErr(null);
    else { setErr(res.error); setValue(current); }
  };

  return (
    <div>
      <BrioInput
        type="text"
        inputMode="numeric"
        maxLength={5}
        value={value}
        disabled={disabled || saving}
        // No placeholder. A greyed "all" in every empty box reads like a value
        // already recorded, the same reason the code-name boxes carry none.
        onChange={(e) => { setValue(e.target.value.replace(/\D/g, '').slice(0, 5)); if (err) setErr(null); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        title={lockedReason
          || 'How many numbers this trade type takes in the next split. Empty shares the balance.'}
        style={{
          width: 54, fontSize: '0.78rem', padding: '0.15rem 0.3rem',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          textAlign: 'center',
          background: err ? 'rgba(169,68,66,0.08)' : (disabled ? 'transparent' : undefined),
          borderColor: err ? CLASH_COLOR : (disabled ? 'var(--brio-border-subtle, #eee)' : undefined),
        }}
      />
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// A trade type's name. A curator double clicks it to rename: the box takes the
// same casing rule as Add, Enter or clicking away saves, Escape cancels. Only
// the label changes. The type keeps its id, so its series, its count and every
// library item tagged with it stay as they were, and no item code moves.
function TradeTypeNameCell({ node, canRename, onRename }) {
  const [editing, setEditing] = useState(false);
  const { label, setLabel, inputRef, readCaps, onChange } = useTradeTypeName(node.label);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Escape and Enter both end in a blur, which would otherwise save a second
  // time or save the edit Escape meant to throw away.
  const doneRef = useRef(false);

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
  }, [editing, inputRef]);

  const start = () => {
    if (!canRename) return;
    doneRef.current = false;
    setLabel(node.label);
    setErr(null);
    setEditing(true);
  };

  const cancel = () => { doneRef.current = true; setErr(null); setEditing(false); };

  const commit = async () => {
    if (doneRef.current || busy) return;
    const value = label.trim();
    if (!value || value === node.label) { cancel(); return; }
    doneRef.current = true;
    setBusy(true);
    const res = await onRename(node.id, value);
    setBusy(false);
    if (res.success) { setErr(null); setEditing(false); } else {
      // Keep the box open on the typed name so it can be fixed, not retyped.
      doneRef.current = false;
      setErr(res.error);
    }
  };

  if (!editing) {
    return (
      <div
        onDoubleClick={start}
        title={canRename ? 'Double click to rename' : undefined}
        style={{ fontSize: '0.88rem', cursor: canRename ? 'text' : undefined, userSelect: canRename ? 'none' : undefined }}
      >
        {node.label}
      </div>
    );
  }

  return (
    <div>
      <BrioInput
        ref={inputRef}
        value={label}
        disabled={busy}
        onKeyDown={(e) => {
          readCaps(e);
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') cancel();
        }}
        onKeyUp={readCaps}
        onChange={(e) => { onChange(e); if (err) setErr(null); }}
        onBlur={commit}
        title={NAME_HINT}
        style={{ fontSize: '0.85rem', padding: '0.15rem 0.4rem', maxWidth: 280 }}
      />
      {err && <div style={{ fontSize: '0.68rem', color: CLASH_COLOR, marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// One trade type. Draggable by its grip within the sub-trade it is anchored
// to — reordering across anchors would change what the type belongs to, which
// is a different operation than arranging a list.
function TradeTypeRow({ node, canCurate, columnEditable, lockedReason, api, onError, validateBand }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id, disabled: !canCurate,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'grid', gridTemplateColumns: GRID_TRADE_TYPE, gap: '0.75rem', alignItems: 'center',
        padding: '0.35rem 0', paddingLeft: 14,
        borderBottom: '1px solid var(--brio-border-subtle, #eee)',
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? 'var(--brio-gold-light, rgba(159,144,42,0.12))' : undefined,
        opacity: isDragging ? 0.85 : 1,
      }}
    >
      {canCurate ? (
        <span
          {...attributes}
          {...listeners}
          title="Drag to reorder within this sub-trade"
          style={{ cursor: 'grab', color: 'var(--brio-text-muted)', display: 'inline-flex', touchAction: 'none' }}
        >
          <GripVertical size={14} />
        </span>
      ) : <span />}
      <TradeTypeNameCell node={node} canRename={canCurate} onRename={api.renameNode} />
      <SeriesCell
        key={`${node.seriesStart}-${node.seriesEnd}`}
        node={node} disabled={!columnEditable} lockedReason={lockedReason}
        onSave={api.setSeries} validate={validateBand} />
      <CountCell
        key={`w-${node.seriesWidth}`}
        node={node} disabled={!columnEditable} lockedReason={lockedReason}
        onSave={api.setSeriesWidth} />
      {canCurate ? (
        <button type="button" title="Remove this trade type"
          onClick={async () => { const r = await api.deleteNode(node.id); if (!r.success) onError(r.error); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--brio-text-muted)' }}>
          <Trash2 size={13} />
        </button>
      ) : <div />}
    </div>
  );
}

function TradeTypeSection({ taxonomy, subtradeGroups, subtradeById, canCurate, api, expanded, onToggle }) {
  const [unlocked, setUnlocked] = useState(false);
  const [err, setErr] = useState(null);
  // Which sub-trade has its add box open. One at a time: two open boxes give
  // two places to type and no way to tell which row a name is going to.
  const [addingUnder, setAddingUnder] = useState(null);
  // Sub-trades whose trade types are folded away. Component state, like the
  // column lock: leaving the page opens everything again.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapsed = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const columnEditable = canCurate && unlocked;
  const lockedReason = !canCurate
    ? 'Only library curators may edit a series'
    : (unlocked ? null : 'Locked — use the lock above the column to edit');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Dragging is scoped to one sub-trade, so the ids compared here are only
  // that row's. api.reorderNodes reorders optimistically and rewrites
  // sort_order in 10-step increments.
  const handleDragEnd = (types) => async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = types.map((t) => t.id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from === -1 || to === -1) return;
    setErr(null);
    const res = await api.reorderNodes(arrayMove(ids, from, to));
    if (!res.success) setErr(res.error);
  };

  // Every sub-trade, in chapter order, whether or not it holds a trade type —
  // the row is where a type is added, so a sub-trade that has none still has
  // to be reachable. Orphans trail at the end under "Unassigned".
  const rows = useMemo(() => {
    if (!taxonomy) return [];
    const byAnchor = new Map();
    taxonomy.tree.forEach((node) => {
      const key = node.mapsToNodeId && subtradeById.has(node.mapsToNodeId) ? node.mapsToNodeId : '__none__';
      if (!byAnchor.has(key)) byAnchor.set(key, []);
      byAnchor.get(key).push(node);
    });
    const flat = subtradeGroups
      .flatMap((g) => g.subs.map((sub, i) => ({ sub, g, first: i === 0 })))
      .map(({ sub, g, first }) => ({
        chapter: first ? { label: g.label, number: g.number } : null,
        key: sub.id,
        label: sub.label,
        seriesStart: sub.seriesStart,
        seriesEnd: sub.seriesEnd,
        types: byAnchor.get(sub.id) || [],
        orphan: false,
      }));
    const orphans = byAnchor.get('__none__');
    if (orphans?.length) {
      // An orphan has no sub-trade, so no range to sit inside and nothing to
      // add under. It renders so it can be seen and re-filed, nothing more.
      flat.push({
        chapter: null, key: '__none__', label: 'Unassigned', seriesStart: null, seriesEnd: null,
        types: orphans, orphan: true,
      });
    }
    return flat;
  }, [taxonomy, subtradeById, subtradeGroups]);

  // The sub-trades after a row, in page order, as the cascade reads them.
  // The Unassigned group is not a sub-trade and never takes a range.
  const belowFor = (index) => rows.slice(index + 1).filter((r) => !r.orphan).map((r) => ({
    id: r.key, label: r.label, seriesStart: r.seriesStart, seriesEnd: r.seriesEnd, types: r.types,
  }));

  // The end of the nearest sub-trade above a row that has a range, or null.
  // A sub-trade given its first count starts right after it.
  const aboveEndFor = (index) => {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (!rows[i].orphan && rows[i].seriesEnd != null) return rows[i].seriesEnd;
    }
    return null;
  };

  if (!taxonomy) return null;

  return (
    <BrioCard className="brio-mb-m">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={onToggle}
      >
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>
            {taxonomy.name} <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>· {taxonomy.nodes.length} nodes</span>
          </h3>
          {taxonomy.description && (
            <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)', marginTop: '0.25rem' }}>
              {taxonomy.description}
            </div>
          )}
          <code style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>{taxonomy.code}</code>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
          {expanded ? '▾ collapse' : '▸ expand'}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', marginBottom: '0.5rem' }}>
            {canCurate
              ? 'Add a trade type on the sub-trade it belongs under. Setting a range on a sub-trade splits it evenly across the trade types under it; a type\'s own band can then be edited within that range. Adding or deleting a type later never re-splits, so a band whose codes are already in a project BOQ never moves.'
              : 'Read-only. Admins and the named library curators can add trade types and set their series.'}
          </div>
          {err && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem',
              padding: '0.4rem 0.6rem', borderRadius: 4,
              background: 'rgba(169,68,66,0.08)', border: '1px solid #a94442', color: '#a94442', fontSize: '0.8rem',
            }}>
              <AlertTriangle size={14} style={{ flexShrink: 0 }} /> {err}
            </div>
          )}

          <div style={{
            display: 'grid', gridTemplateColumns: GRID_TRADE_TYPE, gap: '0.75rem',
            padding: '0 0 0.35rem', borderBottom: '2px solid var(--brio-border, #ddd)',
            fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)',
          }}>
            <div />
            <div>Sub-trade → trade type</div>
            <CodeNameHeader label="Series" canCurate={canCurate} unlocked={unlocked}
              onToggle={() => setUnlocked((v) => !v)} />
            <div style={{ textAlign: 'center' }} title="How many numbers each trade type takes in a split">No</div>
            <div />
          </div>

          {rows.length === 0 && (
            <BrioEmptyState>No sub-trades in the canonical trade taxonomy yet.</BrioEmptyState>
          )}

          {rows.map((row, rowIndex) => (
            <div key={row.key} style={{ marginBottom: '0.35rem' }}>
              {row.chapter && (
                <div style={{
                  margin: '0.9rem 0 0.2rem', padding: '0 0 0.3rem',
                  borderBottom: '1px solid var(--brio-border, #ddd)',
                  fontSize: '0.8rem', fontWeight: 600, color: 'var(--brio-text, #333)',
                }}>
                  {row.chapter.number != null ? `${row.chapter.number}. ` : ''}{row.chapter.label}
                </div>
              )}
              <div style={{
                display: 'grid', gridTemplateColumns: GRID_TRADE_TYPE, gap: '0.75rem',
                alignItems: 'center', padding: '0.4rem 0 0.25rem',
              }}>
                {row.types.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleCollapsed(row.key)}
                    title={collapsed.has(row.key)
                      ? `Show the ${row.types.length} trade type${row.types.length === 1 ? '' : 's'} under ${row.label}`
                      : `Hide the trade types under ${row.label}`}
                    aria-expanded={!collapsed.has(row.key)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', background: 'none',
                      border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0,
                      color: 'var(--brio-text-muted)',
                    }}
                  >
                    {collapsed.has(row.key) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </button>
                ) : <div />}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span style={{
                  fontSize: '0.78rem', fontWeight: 700,
                  color: row.orphan ? '#8a6d00' : 'var(--brio-text, #333)',
                }}>
                  {row.label}
                </span>
                {canCurate && !row.orphan && (
                  <button
                    type="button"
                    // The plus carries no label of its own, so the title and
                    // aria-label have to name the sub-trade it adds under.
                    title={`Add a trade type under ${row.label}`}
                    aria-label={`Add a trade type under ${row.label}`}
                    onClick={() => setAddingUnder((cur) => (cur === row.key ? null : row.key))}
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      background: 'none', border: 'none', borderRadius: 4,
                      cursor: 'pointer', padding: 2, lineHeight: 0,
                      color: addingUnder === row.key ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text-muted)',
                    }}
                  >
                    <Plus size={14} />
                  </button>
                )}
                </div>
                {row.orphan ? <div /> : (
                  <SubtradeSeriesCell
                    row={row}
                    below={belowFor(rowIndex)}
                    api={api}
                    disabled={!columnEditable}
                    lockedReason={lockedReason}
                    onError={setErr}
                  />
                )}
                {/* The sub-trade's own count is the size of its range: typing
                    one moves the range's end, never its start. */}
                {row.orphan ? <div /> : (
                  <SubtradeCountCell
                    key={`n-${row.seriesStart}-${row.seriesEnd}`}
                    row={row}
                    below={belowFor(rowIndex)}
                    aboveEnd={aboveEndFor(rowIndex)}
                    api={api}
                    disabled={!columnEditable}
                    lockedReason={lockedReason}
                    onError={setErr}
                  />
                )}
                {row.orphan ? <div /> : (
                  <SubtradeRestartButton
                    row={row}
                    below={belowFor(rowIndex)}
                    api={api}
                    disabled={!columnEditable}
                    lockedReason={lockedReason}
                    onError={setErr}
                  />
                )}
              </div>
              {addingUnder === row.key && (
                <NewTradeTypeInput
                  onClose={() => setAddingUnder(null)}
                  onAdd={(label) => api.createNode({
                    taxonomyId: taxonomy.id, label, mapsToNodeId: row.key,
                  })}
                />
              )}
              {!collapsed.has(row.key) && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd(row.types)}
              >
                <SortableContext
                  items={row.types.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {row.types.map((node) => (
                    <TradeTypeRow
                      key={node.id}
                      node={node}
                      canCurate={canCurate}
                      columnEditable={columnEditable}
                      lockedReason={lockedReason}
                      api={api}
                      onError={setErr}
                      validateBand={(from, to) => bandWithinSubtrade({
                        start: from, end: to,
                        subStart: row.seriesStart, subEnd: row.seriesEnd,
                      }) && `Outside ${row.label}, which runs `
                        + `${formatSeriesRange(row.seriesStart, row.seriesEnd)}.`}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              )}
            </div>
          ))}
        </div>
      )}
    </BrioCard>
  );
}
function TaxonomiesPage() {
  const api = useBoqTaxonomies();
  const { taxonomies, loading, error, canCurate, refresh } = api;
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };


  // Every brio_trade sub-trade (the child nodes), grouped under its chapter —
  // the list a new trade type is filed against.
  const { subtradeGroups, subtradeById } = useMemo(() => {
    const brio = taxonomies.find((t) => t.code === BRIO_TRADE_TAXONOMY);
    const byId = new Map();
    if (!brio) return { subtradeGroups: [], subtradeById: byId };
    const chapters = new Map(brio.nodes.filter((n) => !n.parentId).map((n) => [n.id, n]));
    // Chapter numbers match the Subtrade template page: the chapter's place
    // among every brio_trade chapter, so a chapter with no sub-trades still
    // holds its number and the rest do not shift.
    const numberByLabel = new Map(
      [...chapters.values()]
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((c, i) => [c.label, i + 1]),
    );
    const order = []; const byChapter = new Map();
    brio.nodes
      .filter((n) => n.parentId)
      .sort((a, b) => {
        const ca = chapters.get(a.parentId)?.sortOrder ?? 0;
        const cb = chapters.get(b.parentId)?.sortOrder ?? 0;
        return (ca - cb) || (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label);
      })
      .forEach((n) => {
        const chapterLabel = chapters.get(n.parentId)?.label || '';
        byId.set(n.id, { id: n.id, label: n.label, chapterLabel });
        if (!byChapter.has(chapterLabel)) { byChapter.set(chapterLabel, []); order.push(chapterLabel); }
        byChapter.get(chapterLabel).push(n);
      });
    return {
      subtradeGroups: order.map((label) => ({
        label, number: numberByLabel.get(label) ?? null, subs: byChapter.get(label),
      })),
      subtradeById: byId,
    };
  }, [taxonomies]);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--brio-text-muted)' }}>Loading taxonomies…</div>;
  }

  if (error) {
    return (
      <BrioCard>
        <h3 style={{ marginTop: 0 }}>Couldn't load taxonomies</h3>
        <p style={{ color: 'var(--brio-text-muted)' }}>{error}</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
          If this says "schema not found" or similar: the <code>boq</code> schema needs to be added to
          Supabase Dashboard → Project Settings → API → Exposed schemas.
        </p>
        <BrioButton onClick={() => refresh()}>Try again</BrioButton>
      </BrioCard>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--brio-text-muted)', margin: 0 }}>
          {taxonomies.length} taxonomies · {taxonomies.reduce((sum, t) => sum + t.nodes.length, 0)} nodes
        </p>
        <BrioButton variant="secondary" onClick={() => refresh()}>Refresh</BrioButton>
      </div>

      {/* Every taxonomy in its stored order. Trade type gets its own editable
          section; the rest list read-only apart from the code name. */}
      {taxonomies.map((t) => (t.code === TRADE_TYPE_TAXONOMY ? (
        <TradeTypeSection
          key={t.id}
          taxonomy={t}
          subtradeGroups={subtradeGroups}
          subtradeById={subtradeById}
          canCurate={canCurate}
          api={api}
          expanded={expanded.has(t.id)}
          onToggle={() => toggle(t.id)}
        />
      ) : (
        <TaxonomySection
          key={t.id}
          taxonomy={t}
          expanded={expanded.has(t.id)}
          onToggle={() => toggle(t.id)}
          canCurate={canCurate}
          onSetShortCode={api.setShortCode}
          onRename={api.renameNode}
          subtradeChapters={subtradeGroups}
          subtradesByGroup={api.subtradesByGroup}
          onToggleSubtrades={api.setTradeGroupSubtrades}
        />
      )))}
    </div>
  );
}

export default TaxonomiesPage;
